import React, {useEffect, useState} from "react";
import {
    Layout,
    Breadcrumb,
    Row,
    Col,
    Table,
    Card,
    Tag,
    Space,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    message, Checkbox, Select, Tabs
} from "antd";
import {Link} from "react-router-dom";
import {DeleteOutlined, EditOutlined, CheckCircleOutlined, PlusOutlined} from "@ant-design/icons";
import Api from "../api";
import {Editor} from "@tinymce/tinymce-react";

const {Content} = Layout;

const AdminStartGuides = () => {
    const [startGuides, setStartGuides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingGuide, setEditingGuide] = useState(null);
    const [form] = Form.useForm();
    const [descriptionHtml, setDescriptionHtml] = useState("");
    const [activeTab, setActiveTab] = useState("default");

    useEffect(() => {
        loadStartGuides(activeTab);
    }, [activeTab]);

    const openModal = (guide = null) => {
        setEditingGuide(guide);
        form.resetFields();

        if (guide) {
            form.setFieldsValue({
                step: guide.step,
                title: guide.title,
                enabled: guide.enabled ?? true,
                type: guide.type ?? "default",
            });
            setDescriptionHtml(guide.description || "");
        } else {
            form.setFieldsValue({
                step: 1,
                title: "",
                enabled: true,
                type: activeTab,
            });
            setDescriptionHtml("");
        }

        setModalVisible(true);
    };

    const loadStartGuides = (type = activeTab) => {
        setLoading(true);

        Api.get(`/admin/v2/start-guides?type=${type}`)
            .then(res => {
                const sorted = [...res.data].sort((a, b) => a.step - b.step);
                setStartGuides(sorted);
            })
            .finally(() => setLoading(false));
    };

    const handleDelete = (id) => {
        Api.delete(`/admin/v2/start-guides/${id}`)
            .then(() => {
                message.success("Start guide deleted");
                loadStartGuides();
            })
            .catch(() => message.error("Failed to delete"));
    };

    const handleSubmit = () => {
        form.validateFields().then(values => {
            console.log(values);
            // TinyMCE description must override the form value if needed
            const payload = {
                ...values,
                description: descriptionHtml,
                enabled: values.enabled ? 1 : 0, // ensure boolean is sent correctly
            };

            if (editingGuide) {
                Api.put(`/admin/v2/start-guides/${editingGuide.id}`, payload)
                    .then(() => {
                        message.success("Start guide updated");
                        loadStartGuides();
                        setModalVisible(false);
                    })
                    .catch(() => message.error("Failed to update"));
            } else {
                Api.post("/admin/v2/start-guides", payload)
                    .then(() => {
                        message.success("Start guide added");
                        loadStartGuides();
                        setModalVisible(false);
                    })
                    .catch(() => message.error("Failed to add"));
            }
        });
    };
    const columns = [
        {
            title: "Step",
            dataIndex: "step",
            width: "10%",
        },
        {
            title: "Title",
            dataIndex: "title",
        },
        {
            title: "Type",
            dataIndex: "type",
            render: (value) => (
                <Tag color={value === "vip" ? "purple" : "blue"}>
                    {value?.toUpperCase()}
                </Tag>
            ),
            width: "10%",
        },
        {
            title: "Description",
            dataIndex: "description",
            render: text => (
                <div style={{maxHeight: 50, overflow: "hidden"}}
                     dangerouslySetInnerHTML={{__html: text || ''}}>
                </div>
            ),
        },
        {
            title: "Completed users count",
            dataIndex: "completions_count",
            render: (_, record) => (
                <Tag>
                    {record.completions_count || 0}
                </Tag>
            ),
            width: "15%",
        },
        {
            title: "Enabled",
            dataIndex: "enabled",
            render: (value) => value ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
            width: "10%",
        },
        {
            title: "Actions",
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined/>} onClick={() => openModal(record)}/>
                    <Button icon={<DeleteOutlined/>} danger onClick={() => handleDelete(record.id)}/>
                </Space>
            ),
            width: "15%",
        },
    ];

    return (
        <Content style={{margin: "0 16px"}}>
            <Breadcrumb style={{margin: "16px 0"}}>
                <Breadcrumb.Item>Admin</Breadcrumb.Item>
                <Breadcrumb.Item>Start Guides</Breadcrumb.Item>
            </Breadcrumb>

            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                items={[
                    {
                        key: "default",
                        label: "New user",
                    },
                    {
                        key: "vip",
                        label: "VIP",
                    },
                ]}
            />

            <div style={{marginBottom: 16}}>
                <Button type="primary" icon={<PlusOutlined/>} onClick={() => openModal()}>
                    Add Start Guide
                </Button>
            </div>

            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={startGuides}
                pagination={{pageSize: 20}}
            />

            <Modal
                title={editingGuide ? "Edit Start Guide" : "Add Start Guide"}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleSubmit}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Step"
                        name="step"
                        rules={[{required: true, message: "Step is required"}]}
                    >
                        <InputNumber min={1} style={{width: "100%"}}/>
                    </Form.Item>

                    <Form.Item
                        label="Type"
                        name="type"
                        initialValue={"default"}
                        rules={[{required: true, message: "Type is required"}]}
                    >
                        <Select
                            options={[
                                {label: "Default", value: "default"},
                                {label: "VIP", value: "vip"},
                            ]}
                        />
                    </Form.Item>

                <Form.Item
                    label="Title"
                    name="title"
                    rules={[{required: true, message: "Title is required"}]}
                >
                    <Input/>
                </Form.Item>
                <Form.Item label="Description">
                    <Editor
                        apiKey="ifldr77em1gvghkwv822nm1q2cv4p0349ju49t01qzamt7af"
                        value={descriptionHtml}
                        init={{
                            height: 200,
                            menubar: false,
                            plugins: "link lists code",
                            toolbar: "undo redo | bold italic underline | bullist numlist | link | code",
                            link_target_list: [
                                {title: "Same tab", value: ""},
                                {title: "New tab", value: "_blank"}
                            ],
                        }}
                        onEditorChange={(content) => setDescriptionHtml(content)}
                    />
                </Form.Item>
                <Form.Item
                    label=""
                    name="enabled"
                    valuePropName="checked" // important for checkbox
                >
                    <Checkbox>Enabled</Checkbox>
                </Form.Item>
            </Form>
        </Modal>
</Content>
)
    ;
};

export default AdminStartGuides;